// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { CheckboxProps } from "@insite/mobius/Checkbox/Checkbox";
import { RadioStyle } from "@insite/mobius/Radio";
import { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useBunzlDispatch } from "@store/Framework/Store/Bunzl/BunzlReducer";
import StepFormInputContext from "@store/Framework/Store/Context/StepFormInputContext";
import StepFormCheckBox from "@store/Library/Components/Bunzl/StepFormComponents/StepFormCheckBox";
import StepFormProvinceSelect from "@store/Library/Components/Bunzl/StepFormComponents/StepFormProvinceSelect";
import StepFormTextField from "@store/Library/Components/Bunzl/StepFormComponents/StepFormTextField";
import BasicDetailsAccountTypeRadio from "@store/Library/Components/CreditForm/StepComponents/BasicDetailsAccountTypeRadio";
import CardBodyComponent from "@store/Library/Widgets/Card/CardBody";
import { useCreditFormContext } from "@store/Library/Widgets/CreditForm/CreditFormContextContainer";
import { RadioGroupPresentationProps } from "@store/MobiusOverrides/RadioGroup/RadioGroup";
import getBooleanProperty from "@toolkit/Framework/Common/Utilities/getBooleanProperty";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

import React from "react";
import { css } from "styled-components";

interface CreditFormStepStyles {
    description?: InjectableCss;
    cardBody?: InjectableCss;
    radioGroup?: RadioGroupPresentationProps;
    billingAndShippingTypography?: TypographyProps;
    subTitleTypography?: TypographyProps;
    toolTip?: TooltipPresentationProps;
    stepFormCheckBox?: CheckboxProps;
    checkBoxWrapper?: InjectableCss;
}

export const creditFormStepStyles: CreditFormStepStyles = {
    checkBoxWrapper: {
        css: css`
            display: flex;
            gap: 2rem;
            width: 100%;
            margin-block: ${pxToRem(8)};
            align-items: center;
        `,
    },
    stepFormCheckBox: {
        css: css`
            width: 100%;
            span[role="checkbox"] {
                border: 1px solid ${getColor("primary")};
                & + label {
                    color: black;
                }
                &[aria-disabled="true"] {
                    &[aria-checked="true"] {
                        background: ${getColor("primary")};
                        color: white;
                    }
                    &[aria-checked="false"] {
                        background: transparent;
                        border: 1px solid ${getColor("primary")};
                    }
                }
            }
        `,

        typographyProps: {
            css: css`
                padding-top: ${pxToRem(2)};
            `,
        },
    },
    toolTip: {
        mergeCss: true,
        cssOverrides: {
            tooltipWrapper: css`
                top: 0;
            `,
        },
    },
    subTitleTypography: {
        variant: "h5",
        css: css`
            width: 100%;
            margin-block: 0;
        `,
    },
    billingAndShippingTypography: {
        variant: "h4",
        css: css`
            width: 100%;
            margin-block: 0;
        `,
    },

    radioGroup: {
        css: css`
            > ${RadioStyle} {
                margin-top: 0;
                margin-right: 0;
            }
            display: flex;
            gap: ${pxToRem(20)};
            align-items: center;
            width: 100%;
            > legend {
                font-size: ${pxToRem(14)};
                float: left;
            }
        `,
    },
    cardBody: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: ${pxToRem(20)};
            padding-top: 0;
            scrollbar-gutter: stable;
        `,
    },
    description: {
        css: css`
            opacity: 0.4;
            color: black;
        `,
    },
};

const getShippingWrapperStyles = (editable: boolean) => css`
    display: flex;
    flex-wrap: wrap;
    gap: 1.25rem;
    opacity: ${editable ? 1 : 0.6};
    width: 100%;
`;
export interface CreditFormStepProps {
    hasSubmitted: boolean;
    editable?: boolean;
    stepIndex: number;
}

const shippingKeys = [
    "shippingCompanyAddress",
    "shippingCity",
    "shippingPostalCode",
    "shippingPhoneNumber",
    "shippingProvince",
];
const BasicDetailsForm: React.FC<CreditFormStepProps> = ({ hasSubmitted, editable = true, stepIndex }) => {
    const { storeKey } = useCreditFormContext();
    const bunzlDispatch = useBunzlDispatch();
    const billingAddressForShipping = getBooleanProperty(
        useAppSelector(
            state =>
                state.bunzl.pages.stepForm[storeKey]?.steps[stepIndex]?.inputs?.useBillingAddressForShipping?.value,
        ),
    );

    const shippingPrevix = billingAddressForShipping ? "billing" : "shipping";

    const handleUseBillingAddressForShipping = (value: boolean) => {
        if (value === true) {
            shippingKeys.forEach(key => {
                bunzlDispatch({
                    type: "Data/StepForm/RemoveInput",
                    formKey: storeKey,
                    stepIndex,
                    inputKey: key,
                });
            });
        }
    };

    const stepFormInputContextData = React.useMemo(
        () => ({ currentStep: stepIndex, editable, hasSubmitted }),
        [stepIndex, editable, hasSubmitted],
    );
    const shippingEditable = !billingAddressForShipping;
    const shippingContextData = React.useMemo(
        () => ({ currentStep: stepIndex, editable: shippingEditable, hasSubmitted }),
        [stepIndex, shippingEditable, hasSubmitted],
    );
    return (
        <StepFormInputContext.Provider value={stepFormInputContextData}>
            <CardBodyComponent extendedStyles={creditFormStepStyles.cardBody}>
                <BasicDetailsAccountTypeRadio />
                <StepFormTextField
                    validation="companyName"
                    required
                    inputKey="companyName"
                    label={translate("Company Name")}
                    placeholder={translate("Enter Company Name")}
                    autoComplete="organization"
                />
                <StepFormTextField
                    required
                    validation="businessType"
                    inputKey="businessType"
                    label={translate("Type of Business")}
                    placeholder={translate("Enter The Type of Business")}
                    disabled={!editable}
                />
                <StepFormTextField
                    validation="email"
                    required
                    inputKey="emailForInvoices"
                    label={translate("Email for Invoices")}
                    placeholder={translate("Enter Email for Invoices")}
                />
                <StepFormTextField
                    inputKey="websiteAddress"
                    label={translate("Website Address")}
                    placeholder={translate("Enter Website Address")}
                />
                <StepFormTextField
                    inputKey="previousBusinessNames"
                    label={translate("List any DBA's or Previous Company Names")}
                    placeholder={translate("Enter DBA's or Previous Company Names")}
                    autoComplete="off"
                />
                <StepFormTextField
                    inputKey="yearsInOperation"
                    label={translate("Years in Operation")}
                    placeholder={translate("E.G. 5")}
                    required
                    validation="yearsInOperation"
                    type="number"
                    autoComplete="off"
                />
                <Typography {...creditFormStepStyles.subTitleTypography}>{translate("Accounts Payable")}</Typography>
                <StepFormTextField
                    validation="contactName"
                    variant="small"
                    required
                    inputKey="accountContactName"
                    label={translate("Contact Name")}
                    placeholder={translate("Enter Contact Name")}
                />
                <StepFormTextField
                    required
                    inputKey="accountPhoneNumber"
                    label={translate("Phone Number")}
                    placeholder={translate("Enter Phone Number")}
                    variant="small"
                    validation="phoneNumber"
                />
                <StepFormTextField
                    required
                    variant="small"
                    validation="email"
                    inputKey="accountEmail"
                    label={translate("Account Email")}
                    placeholder={translate("Enter AccountEmail")}
                />
                <Typography {...creditFormStepStyles.subTitleTypography}>{translate("Billing Address")}</Typography>

                <StepFormTextField
                    required
                    validation="address"
                    inputKey="billingCompanyAddress"
                    label={translate("Street Address")}
                    placeholder={translate("Enter Street Address")}
                />
                <StepFormTextField
                    validation="city"
                    inputKey="billingCity"
                    label={translate("City")}
                    required
                    placeholder={translate("Enter City")}
                />
                <StepFormProvinceSelect
                    inputKey="billingProvince"
                    label={translate("Province")}
                    hasSubmitted={hasSubmitted}
                    placeholder={translate("Enter Province")}
                    disabled={!editable}
                    stepOverride={stepIndex}
                    validation="province"
                    required
                />
                <StepFormTextField
                    inputKey="billingPostalCode"
                    label={translate("Postal Code")}
                    placeholder={translate("Enter Postal Code")}
                    variant="verySmall"
                    required
                    validation="postalCode"
                />
                <StepFormTextField
                    inputKey="billingPhoneNumber"
                    validation="phoneNumber"
                    required
                    label={translate("Phone Number")}
                    placeholder={translate("Enter Phone Number")}
                    autoComplete="tel"
                    type="tel"
                    variant="verySmall"
                />
                <StepFormTextField
                    validation="faxNumber"
                    inputKey="billingFaxNumber"
                    label={translate("Fax Number")}
                    placeholder={translate("Enter Fax Number")}
                    autoComplete="tel"
                    type="tel"
                    variant="verySmall"
                />

                <StyledWrapper {...creditFormStepStyles.checkBoxWrapper}>
                    <StepFormCheckBox
                        inputKey="useBillingAddressForShipping"
                        changeSideEffect={handleUseBillingAddressForShipping}
                        {...creditFormStepStyles.stepFormCheckBox}
                    >
                        {translate("Use Billing Address For Shipping")}
                    </StepFormCheckBox>
                </StyledWrapper>
                <StepFormInputContext.Provider value={shippingContextData}>
                    <StyledWrapper css={getShippingWrapperStyles(shippingEditable || !editable)}>
                        <Typography {...creditFormStepStyles.subTitleTypography}>
                            {translate("Shipping Address")}
                        </Typography>
                        <StepFormTextField
                            validation="address"
                            required={shippingEditable}
                            inputKey={`${shippingPrevix}CompanyAddress`}
                            label={translate("Street Address")}
                            placeholder={translate("Enter Street Address")}
                        />
                        <StepFormTextField
                            validation="city"
                            required={shippingEditable}
                            label={translate("City")}
                            placeholder={translate("Enter City")}
                            inputKey={`${shippingPrevix}City`}
                        />
                        <StepFormProvinceSelect
                            validation="province"
                            required={shippingEditable}
                            label={translate("Province")}
                            inputKey={`${shippingPrevix}Province`}
                            placeholder={translate("Enter Province")}
                        />
                        <StepFormTextField
                            validation="postalCode"
                            label={translate("Postal Code")}
                            placeholder={translate("Enter Postal Code")}
                            inputKey={`${shippingPrevix}PostalCode`}
                            variant="small"
                            required={shippingEditable}
                        />
                        <StepFormTextField
                            validation="phoneNumber"
                            label={translate("Phone Number")}
                            placeholder={translate("Enter Phone Number")}
                            inputKey={`${shippingPrevix}PhoneNumber`}
                            autoComplete="tel"
                            type="tel"
                            variant="small"
                            required={shippingEditable}
                        />
                    </StyledWrapper>
                </StepFormInputContext.Provider>
            </CardBodyComponent>
        </StepFormInputContext.Provider>
    );
};
export default BasicDetailsForm;
